'use client';

import { useMemo } from 'react';
import { cn } from '@fshr/shared-react';
import { OverlayCarousel } from '../carousel/overlay-carousel/overlay-carousel-component';
import { ImageGalleryPagination } from './image-gallery-dots';
import { ImageGalleryArrows } from './image-gallery-arrows';
import { Image } from '../components';
import { ImageStructure } from '../types';

type ImageGalleryProps = {
	images?: ImageStructure[];
	className?: string;
};

const ImageGalleryCarousel = ({ images, className }: ImageGalleryProps) => {
	const carouselItems = useMemo(() => {
		return images?.map((image, index) => (
			<div
				key={index}
				className={cn('relative w-full min-w-full aspect-[16/9]', {
					'bg-white': image.scaleImage,
				})}
			>
				<Image
					roughSize="m"
					src={image.url}
					alt={image.altText ?? `Gallery image ${index + 1}`}
					className={cn(
						'w-full h-full min-w-full min-h-full max-w-full max-h-full',
						image.scaleImage ? 'object-contain' : 'object-cover'
					)}
				/>
			</div>
		));
	}, [images]);

	return (
		<div className={cn('flex flex-col group relative', className)}>
			<OverlayCarousel
				isFullWidth={false}
				disableButtons={false}
				gap={0}
				carouselItemsContainer="gap-[0px]! w-full!"
				itemClassName="w-full h-full"
				CustomPagination={ImageGalleryPagination}
				CustomArrows={ImageGalleryArrows}
			>
				{carouselItems}
			</OverlayCarousel>
		</div>
	);
};

export default function ImageGalleryComponent({ images, className }: ImageGalleryProps) {
	// Return null if no images
	if (!images || images.length === 0) {
		return null;
	}

	// Single image case - just show the image without carousel
	if (images.length === 1) {
		return (
			<div className={cn('w-full', className)}>
				<div
					className={cn('relative w-full aspect-[16/9]', {
						'bg-white': images[0].scaleImage,
					})}
				>
					<img
						src={images[0].url}
						alt={images[0].altText ?? 'Gallery image'}
						className={cn(
							'w-full h-full',
							images[0].scaleImage ? 'object-contain' : 'object-cover'
						)}
					/>
				</div>
			</div>
		);
	}

	// Multiple images - use carousel with custom rendering
	return <ImageGalleryCarousel images={images} className={className} />;
}
